import { ErrorHandler, Injectable, Injector, inject } from "@angular/core";
import { handleRedirectOptions, scopes, signinAuthority } from "@commonConfig/msalb2c.config";
import { LOGIN_HINT_KEY } from "@commonHelpers/auth-needed";
import { isInIframe } from "@commonHelpers/iframe-helper";
import { isNullish, isNullishOrEmpty } from "@commonHelpers/math-utils";
import type { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { firstValueFrom, Subject } from "rxjs";
import { AUTH_CLIENT } from "@commonInterfaces/iAuthAccess";
import { environment } from "@environments/environment";

@Injectable({
	providedIn: 'root'
})
export class MsalB2CService {

	// Über AUTH_CLIENT statt direkt MsalService: dieser Dienst wird eifrig erzeugt (der
	// Interceptor und die Kopfzeile fragen ihn beim ersten Rendern), und eine
	// Konstruktorinjektion von MsalService hielt @azure/msal-* im initialen Bundle - 323 kB roh,
	// auch für anonyme Besucher und Crawler. Die MSAL-Typen unten sind `import type` und damit
	// beim Kompilieren weg.
	private readonly authService = inject(AUTH_CLIENT);

	// Fehler gehen an den ErrorHandler der Anwendung statt auf die Konsole. ErrorHandler aus
	// @angular/core ist dabei die neutrale Naht: Common.Angular darf nichts aus einem
	// Gui-Projekt kennen. Im Portal landet der Fehler damit im ErrorHandlerService, der ihn
	// protokolliert und an Application Insights meldet; im Catalog Manager fällt dessen Handler
	// auf Angulars Standard durch, also weiterhin auf die Konsole.
	//
	// Bewusst der Injector und nicht `inject(ErrorHandler)`: eine Injektion hier ist ein
	// Zirkelbezug. Dieser Dienst wird vom MsalB2CInterceptor gebraucht, der in
	// HTTP_INTERCEPTORS steht; der ErrorHandler des Portals zieht Dienste mit HttpClient nach,
	// und HttpClient braucht wieder HTTP_INTERCEPTORS. Angular meldet das als NG0200 für
	// HTTP_INTERCEPTORS und MsalB2CService, der Start bricht ab und die Anwendung bleibt leer.
	// Über den Injector wird der ErrorHandler erst im Fehlerfall aufgelöst, also lange nach
	// dem Start - dann ist der Ring nicht mehr geschlossen.
	private readonly injector = inject(Injector);

	// Einzige Quelle ist auth-needed, weil die Startentscheidung denselben Schlüssel liest.
	private static readonly loginHintStorageKey = LOGIN_HINT_KEY;

	private iframeLoginPopupErrorSubscription = new Subject()
	private ssoRestoreAttempted = false;

	public async signup(loginLanguage: string): Promise<void | AuthenticationResult> {
		if (!isNullish(environment?.msal) && !isNullish(environment?.msal?.signUpFlow)) {
			return this.invokeFlow(environment.msal.signUpFlow, loginLanguage);
		}
		throw new Error('environment not set');
	}

	public async login(loginLanguage: string): Promise<void | AuthenticationResult> {
		if (!isNullish(environment?.msal) && !isNullish(environment?.msal?.signInFlow)) {
			return this.invokeFlow(environment.msal.signInFlow, loginLanguage);
		}
		throw new Error('environment not set');
	}

	public async logout(): Promise<void | AuthenticationResult> {
		return this.invokeFlow('logout', 'logout');
	}

	public isLoggedIn(): boolean {
		return this.authService.getAllAccounts().length > 0;
	}

	public acquireTokenSilent(scopesUrls: string[]): Promise<AuthenticationResult> {

		const account = this.getAllAccounts()[0];
		return firstValueFrom(this.authService.acquireTokenSilent({ account, scopes: scopesUrls }));
	}


	public async acquireIdTokenSilent(): Promise<AuthenticationResult> {
		if (!isNullish(environment?.msal) && !isNullish(environment?.msal?.clientId)) {
			return this.acquireTokenSilent([environment.msal.clientId]);
		}
		throw new Error('environment not set');

	}

	async waitForIsLogin() {
		await firstValueFrom(this.authService.initialize());
		return this.isLoggedIn();
	}

	public getIframeLoginPopupErrorSubscription() {
		return this.iframeLoginPopupErrorSubscription;
	}

	public async iframeRefreshLoggedIn() {
		await firstValueFrom(this.authService.initialize());
		await firstValueFrom(this.authService.handleRedirect(handleRedirectOptions));
		try {
			await firstValueFrom(this.authService.ssoSilent({ scopes }));
		} catch (silentError) {
			// Beide Fehler werden protokolliert statt geschluckt. Im eingebetteten Frame
			// entscheidet genau ihr Code, woran der Anmeldeweg hängt, und ohne Protokoll ist das
			// von außen nicht unterscheidbar: "login_required" oder "interaction_required" heißt,
			// dass der verschachtelte Frame das Sitzungscookie des Anmeldedienstes nicht
			// mitsenden konnte (Speicherpartitionierung in Firefox); ein Zeitüberschreitungs-
			// oder Bridge-Fehler heißt, dass die Antwort da war, den Frame aber nicht erreicht
			// hat. Beim Popup unterscheidet der Code ebenso: ein Fenster-/Blockerfehler ist
			// etwas anderes als eine Zeitüberschreitung beim Warten auf die Antwort.
			this.report("Stiller Anmeldeversuch im eingebetteten Frame fehlgeschlagen", silentError);
			try {
				await firstValueFrom(this.authService.acquireTokenPopup({ scopes }));
			} catch (popupError) {
				this.report("Anmeldung per Popup im eingebetteten Frame fehlgeschlagen", popupError);
				this.iframeLoginPopupErrorSubscription.next(null);
			}
		}
	}

	/**
	 * Meldet einen MSAL-Fehler an den ErrorHandler der Anwendung.
	 *
	 * Die auswertbaren Felder stehen in der Meldung und nicht in einem zweiten Argument, weil
	 * Application Insights aus einer Ausnahme nur `message` und `stack` überträgt - ein
	 * Detailobjekt daneben käme dort nie an. `errorCode` und `subError` sind genau das, was
	 * einen dieser Fehler unterscheidbar macht.
	 *
	 * Der ursprüngliche Fehler hängt als `cause` daran, damit in der Konsole der echte
	 * Stapelspeicher erreichbar bleibt.
	 */
	private report(message: string, error: unknown): void {
		const details = MsalB2CService.describeError(error);
		this.injector.get(ErrorHandler).handleError(
			new Error(`${message} (${JSON.stringify(details)})`, { cause: error })
		);
	}

	/**
	 * Zieht die auswertbaren Felder aus einem MSAL-Fehler heraus. `errorCode` ist das, was
	 * zählt - die Meldung selbst ist oft nur ein Verweis auf die Fehlerdokumentation.
	 */
	private static describeError(error: unknown): Record<string, unknown> {
		const candidate = error as { errorCode?: string; errorMessage?: string; subError?: string; name?: string; message?: string } | null;
		return {
			errorCode: candidate?.errorCode ?? "-",
			subError: candidate?.subError ?? "-",
			name: candidate?.name ?? "-",
			message: (candidate?.errorMessage ?? candidate?.message ?? String(error)).slice(0, 300),
		};
	}

	public async isLoggedInByAcquireTokenSilent() {
		await firstValueFrom(this.authService.initialize());
		await firstValueFrom(this.authService.handleRedirect(handleRedirectOptions)); // This will no longer throw this error since initialize completed before this was invoked
		return this.acquireIdTokenSilent();
	}

	public getAllAccounts(): AccountInfo[] {
		return this.authService.getAllAccounts();
	}

	/** IdpId des angemeldeten Benutzers; entspricht dem oid-Claim, den die APIs als IdpId speichern. */
	public getCurrentIdpId(): string | undefined {
		const idpId = this.getAllAccounts()[0]?.localAccountId;
		return isNullishOrEmpty(idpId) ? undefined : idpId;
	}

	/** E-Mail-Adresse des angemeldeten Benutzers. */
	public getCurrentUserEmail(): string | undefined {
		const account = this.getAllAccounts()[0];
		const claims = account?.idTokenClaims as { emails?: string[] } | undefined;
		const email = claims?.emails?.[0] ?? account?.username;
		return isNullishOrEmpty(email) ? undefined : email;
	}

	public persistLoginHint(): void {
		const account = this.getAllAccounts()[0];
		const claims = account?.idTokenClaims as any;
		const loginHint: string | undefined = claims?.login_hint ?? claims?.emails?.[0] ?? account?.username;
		if (isNullishOrEmpty(loginHint)) {
			return;
		}
		try {
			localStorage.setItem(MsalB2CService.loginHintStorageKey, loginHint);
		} catch {

		}
	}

	public clearPersistedLoginHint(): void {
		try {
			localStorage.removeItem(MsalB2CService.loginHintStorageKey);
		} catch {

		}
	}

	private getPersistedLoginHint(): string | undefined {
		try {
			return localStorage.getItem(MsalB2CService.loginHintStorageKey) ?? undefined;
		} catch {
			return undefined;
		}
	}

	// msal-browser v4 verschlüsselt den localStorage-Cache mit einem Schlüssel aus einer Session-Cookie,
	// die Tokens überleben deshalb keinen Browser-Neustart. Die persistente SSO-Cookie von B2C
	// ("Angemeldet bleiben") überlebt ihn, ein einzelner stiller SSO stellt die Sitzung damit wieder her.
	public async trySilentSsoRestore(): Promise<boolean> {
		// Ohne geladenes MSAL gibt es keinen stillen Wiederaufbau. Der Rückfall auf `false` ist
		// wichtig, nicht kosmetisch: `true` heißt für subscribeState() "Wiederaufbau läuft,
		// handleLogin() später", und dann bliebe die Initialisierung stehen. Regulär tritt der Fall
		// nicht ein, weil ein gespeicherter Anmeldehinweis den MSAL-Zweig anfordert - dies ist das
		// Netz darunter.
		if (!this.authService.isAuthenticationAvailable) {
			return false;
		}
		if (this.ssoRestoreAttempted || isInIframe() || this.isLoggedIn() || isNullish(environment?.msal)) {
			return false;
		}
		const loginHint = this.getPersistedLoginHint();
		if (isNullish(loginHint)) {
			return false;
		}
		this.ssoRestoreAttempted = true;
		try {
			await firstValueFrom(this.authService.ssoSilent({ authority: signinAuthority, scopes: this.getScopes(), loginHint }));
			this.persistLoginHint();
		} catch (error) {
			if (this.authService.isInteractionRequired(error)) {
				this.clearPersistedLoginHint();
			}
			// `false` melden, nicht `true`: der Rückgabewert sagt subscribeState(), ob ein
			// Wiederaufbau läuft und handleLogin() deshalb einem weiteren
			// inProgress$ === None überlassen werden kann. Nach einem gescheiterten ssoSilent
			// kommt das nicht zwangsläufig - dann bliebe handleLogin() dauerhaft aus, und mit
			// ihm refreshMenu, initWatchlist und der Übergang von authStateSignal auf `loaded`.
			// Das Kontomenü hing in diesem Fall für immer im Ladezustand.
			this.report("Stiller SSO-Wiederaufbau fehlgeschlagen, Start läuft als abgemeldet weiter", error);
			return false;
		}
		return true;
	}

	private async invokeFlow(flowName: string, loginlanguage?: string): Promise<void | AuthenticationResult> {

		if (flowName === 'logout') {
			// msal-angular 6 kennt nur noch logoutRedirect und logoutPopup; das pauschale logout()
			// ist entfernt. Redirect ist die richtige Variante, weil interactionType im gesamten
			// Projekt InteractionType.Redirect ist.
			return firstValueFrom(this.authService.logoutRedirect());
		}
		if (!isNullish(environment?.msal)) {
			const authority = `${"https://" + environment.msal.tenant + ".b2clogin.com/" + environment.msal.tenant + ".onmicrosoft.com/"}${flowName}`;
			return firstValueFrom(this.authService.loginRedirect({
				extraQueryParameters: { ui_locales: loginlanguage ?? "" },
				authority,
				scopes: this.getScopes()
			}));
		} else {
			throw new Error('environment not set');
		}
	}



	private getScopes(): string[] {
		if (!isNullish(environment?.msal)) {
			return [`https://${environment.msal.tenant}.onmicrosoft.com/${environment.msal.middlewareAppIdUriSuffix}/${environment.msal.apiAccessScope}`];
		} else {
			throw new Error('environment not set');
		}
	}


	public async getAccessToken(): Promise<string> {
		try {
			const result = await this.acquireTokenSilent(this.getScopes());

			return result.accessToken;
		}
		catch {
			return '';
		}
	}

}
